import React from 'react';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/system';

import { useAccount } from 'catering/context/providers/AccountProvider';
import { Container } from 'components/Containers';
import { useIsDesktop } from 'styles/hooks/mediaQueries';
import DarkCover from 'components/DarkCover';
import Hero from 'components/Hero';
import MenuLink from 'catering/components/Menu/MenuLink';
import MenuHeroLocationDetails from 'catering/components/Menu/MenuHeroLocationDetails';
import { LINK_TO_BENTO_WEBSITE } from 'bento-ordering/base/constants/endpoints';
import muiPropForwarding from 'styles/helpers/muiPropForwarding';
import { getSectionNavColor } from 'styles/helpers/getThemeStyles';
import InfoBar, { InfoBarWithLogo } from 'components/InfoBar';
import { OPACITY_5 } from 'constants/opacity';
import Avatar, { SQUARE } from 'components/Avatar';
import { getHasHeroImage } from 'helpers/getHasHeroImage';
import {
  ITEM_DESCRIPTION_AND_PRICES,
  MENU_ITEM_NAMES
} from 'styles/constants/appearanceSectionNames';

const HALF_LOGO_HEIGHT = '90px';

const Root = styled('div')(({ theme }) => ({
  position: 'relative',
  top: theme.spacing(8)
}));

const HeroContainer = styled('div', {
  shouldForwardProp: muiPropForwarding('hasHeroImage')
})(({ theme, hasHeroImage }) => {
  let opacity = {};
  if (!hasHeroImage) {
    opacity = { opacity: OPACITY_5 };
  }
  return {
    position: 'relative',
    minHeight: theme.spacing(11),
    backgroundColor: hasHeroImage
      ? theme.palette.mediumTofu
      : theme.palette.secondary.main,
    [theme.breakpoints.up('md')]: {
      minHeight: theme.spacing(9)
    },
    ...opacity
  };
});

const StyledContainer = styled(Container, {
  shouldForwardProp: muiPropForwarding('hasLogo')
})(({ theme, hasLogo }) => {
  let bottom = {};
  if (hasLogo) {
    bottom = { bottom: HALF_LOGO_HEIGHT };
  }

  return {
    '&.MuiContainer-root': {
      position: 'relative',
      bottom: theme.spacing(8),
      display: 'flex',
      justifyContent: 'center',
      maxWidth: '1254px',
      [theme.breakpoints.up('md')]: {
        bottom: theme.spacing(5),
        justifyContent: 'flex-start',
        alignItems: 'start'
      },
      [theme.breakpoints.down('lg')]: {
        padding: '0 16px'
      },
      [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
        alignItems: 'center',
        ...bottom
      }
    }
  };
});

const AvatarWrapper = styled('div')({
  margin: '0 auto'
});

const Details = styled('div', {
  shouldForwardProp: muiPropForwarding('hasLogo')
})(({ theme, hasLogo }) => ({
  textAlign: 'center',
  [theme.breakpoints.up('md')]: {
    marginTop: '32px',
    marginLeft: hasLogo && '16px',
    textAlign: 'left',
    flexGrow: 2
  },
  [theme.breakpoints.down('md')]: {
    marginTop: !hasLogo && '40px'
  }
}));

const HeaderRowWrapper = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    flexGrow: 2,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  }
}));

const Header = styled(Typography, {
  shouldForwardProp: muiPropForwarding('hasLocationDescription')
})(({ theme, hasLocationDescription }) => ({
  color: getSectionNavColor(theme),
  marginBottom: 3,
  fontSize: 20,
  [theme.breakpoints.up('md')]: {
    marginRight: '24px',
    marginTop: hasLocationDescription ? 35 : 45
  },
  [theme.breakpoints.down('sm')]: {
    marginTop: '20px'
  }
}));

const MenuLinkWrapper = styled('div', {
  shouldForwardProp: muiPropForwarding('hasLocationDescription')
})(({ theme, hasLocationDescription }) => ({
  [theme.breakpoints.up('md')]: {
    marginTop: hasLocationDescription ? 35 : 45,
    marginBottom: 3
  },
  marginTop: 10
}));

const Description = styled(Typography)(({ theme }) => ({
  color: getSectionNavColor(theme),
  padding: '0 16px',
  marginTop: 7,
  marginBottom: 8,
  display: 'inline-block',
  wordBreak: 'normal',
  [theme.breakpoints.up('md')]: {
    padding: '0 !important'
  }
}));

function MenuHero({
  cart,
  menu,
  location,
  shouldDisplayMenuLink,
  displayLocationInformationOnLayout = true
}) {
  const { logo, logoDestinationUrl } = useAccount();
  const isDesktop = useIsDesktop();
  const hasLocationDescription = !!location.description;
  const hasLogo = !!logo;
  const { heroImage, name } = menu;
  const hasHeroImage = getHasHeroImage(heroImage);

  const fulfillmentMethod = cart.catering_fulfillment_method;

  const shouldDisplayMenuLinkOnDesktop = shouldDisplayMenuLink && isDesktop;
  const shouldDisplayMenuLinkOnMobile = shouldDisplayMenuLink && !isDesktop;

  const InfoBarComponent = hasLogo ? InfoBarWithLogo : InfoBar;

  const headerStyles = { variant: MENU_ITEM_NAMES };
  const descriptionStyles = { variant: ITEM_DESCRIPTION_AND_PRICES };

  return (
    <Root>
      <HeroContainer hasHeroImage={hasHeroImage}>
        {hasHeroImage && (
          <Hero image={heroImage}>
            <DarkCover dataTestId="dark-cover" background={heroImage.url} />
          </Hero>
        )}
      </HeroContainer>
      <StyledContainer hasLogo={hasLogo}>
        <InfoBarComponent>
          {hasLogo && (
            <AvatarWrapper>
              <Avatar
                src={logo}
                path={logoDestinationUrl || LINK_TO_BENTO_WEBSITE}
                shape={SQUARE}
              />
            </AvatarWrapper>
          )}
          <Details hasLogo={hasLogo}>
            <HeaderRowWrapper>
              <Header
                hasLocationDescription={hasLocationDescription}
                {...headerStyles}
              >
                {location.location.name}
              </Header>
              {shouldDisplayMenuLinkOnDesktop && (
                <MenuLinkWrapper
                  hasLocationDescription={hasLocationDescription}
                >
                  <MenuLink
                    menuName={name}
                    fulfillmentMethod={fulfillmentMethod}
                  />
                </MenuLinkWrapper>
              )}
            </HeaderRowWrapper>
            {hasLocationDescription && (
              <Description component="span" {...descriptionStyles}>
                {location.description}
              </Description>
            )}

            <MenuHeroLocationDetails
              locationAddress={location.location.street}
              displayLocationInformationOnLayout={
                displayLocationInformationOnLayout
              }
              cart={cart}
            />
          </Details>
        </InfoBarComponent>
        {shouldDisplayMenuLinkOnMobile && (
          <MenuLinkWrapper hasLocationDescription={hasLocationDescription}>
            <MenuLink menuName={name} fulfillmentMethod={fulfillmentMethod} />
          </MenuLinkWrapper>
        )}
      </StyledContainer>
    </Root>
  );
}

MenuHero.propTypes = {
  cart: PropTypes.object.isRequired,
  displayLocationInformationOnLayout: PropTypes.bool,
  location: PropTypes.shape({
    location: PropTypes.shape({
      name: PropTypes.string,
      street: PropTypes.string
    }),
    description: PropTypes.string
  }).isRequired,
  menu: PropTypes.shape({
    lead_time: PropTypes.number,
    heroImage: PropTypes.shape({
      src: PropTypes.string,
      url: PropTypes.string,
      alt_text: PropTypes.string
    }),
    name: PropTypes.string
  }).isRequired,
  shouldDisplayMenuLink: PropTypes.bool.isRequired
};

export default MenuHero;
